import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Popover,
    Stack,
    TextField,
    Typography,
    styled,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SketchPicker } from "react-color";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { theme } from "../../../theme";
import { StatusLevel, StatusLevelData } from "../../../types/serviceModuleTypes";
import {
    StatusLevelValidationErrors,
    parseIntegerInput,
    validateStatusLevel,
} from "./statusLevelValidation";

const DEFAULT_LEVEL_COLOR = "#FFD700";

const StyledColorPreview = styled(Box)(({ color }: { color: string }) => ({
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: color,
    border: `2px solid ${theme.palette.divider}`,
    cursor: "pointer",
    "&:hover": {
        boxShadow: "0 0 8px rgba(0, 0, 0, 0.15)",
        border: `2px solid ${theme.palette.primary.main}`,
        transform: "scale(1.1)",
        transition: "transform 0.15s ease",
    },
}));

interface Props {
    onClose: () => void;
    onSave: (data: StatusLevelData, id: string | undefined) => void;
    level?: StatusLevel;
    siblings: StatusLevel[];
}

export default function EditStatusLevelDialog(props: Props) {
    const { t } = useTranslation();

    const [name, setName] = useState(props.level?.name ?? "");
    const [colorHex, setColorHex] = useState(props.level?.colorHex ?? DEFAULT_LEVEL_COLOR);
    const [payoutPoints, setPayoutPoints] = useState(props.level?.payoutPoints.toString() ?? "");
    const [minPoints, setMinPoints] = useState(props.level?.minPoints.toString() ?? "");
    const [maxPoints, setMaxPoints] = useState(props.level?.maxPoints?.toString() ?? "");
    const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
    const [errors, setErrors] = useState<StatusLevelValidationErrors>({});

    const isEdit = !!props.level;
    const dialogTitle = isEdit
        ? t("serviceModulesPage.statusPage.dialog.editTitle")
        : t("serviceModulesPage.statusPage.dialog.createTitle");
    const maxPointsIsOpenEnded = maxPoints.trim().length === 0;

    const handleSave = () => {
        const candidate = {
            name,
            payoutPoints: parseIntegerInput(payoutPoints),
            minPoints: parseIntegerInput(minPoints),
            maxPoints: maxPointsIsOpenEnded ? null : parseIntegerInput(maxPoints),
            maxPointsIsOpenEnded,
        };
        const validationErrors = validateStatusLevel(candidate, props.siblings);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }
        props.onSave(
            {
                name: name.trim(),
                colorHex,
                payoutPoints: candidate.payoutPoints as number,
                minPoints: candidate.minPoints as number,
                maxPoints: candidate.maxPoints,
            },
            props.level?.id
        );
        props.onClose();
    };

    return (
        <Dialog open={true} onClose={props.onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    label={t("serviceModulesPage.statusPage.dialog.nameLabel")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name ? t(errors.name) : undefined}
                    sx={{ mt: 1 }}
                />

                <Stack direction="row" sx={{ alignItems: "center", gap: 2, mt: 3 }}>
                    <Typography variant="body1">
                        {t("serviceModulesPage.statusPage.dialog.colorLabel")}
                    </Typography>
                    <StyledColorPreview
                        color={colorHex}
                        onClick={(e: React.MouseEvent<HTMLElement>) =>
                            setColorPickerAnchor(e.currentTarget)
                        }
                    />
                </Stack>
                <Popover
                    open={Boolean(colorPickerAnchor)}
                    anchorEl={colorPickerAnchor}
                    onClose={() => setColorPickerAnchor(null)}
                >
                    <SketchPicker
                        color={colorHex}
                        onChange={(color) => setColorHex(color.hex)}
                    />
                </Popover>

                <Stack direction="row" sx={{ gap: 2, mt: 3 }}>
                    <TextField
                        fullWidth
                        label={t("serviceModulesPage.statusPage.dialog.minPointsLabel")}
                        value={minPoints}
                        onChange={(e) => setMinPoints(e.target.value)}
                        error={!!errors.minPoints}
                        helperText={errors.minPoints ? t(errors.minPoints) : undefined}
                    />
                    <TextField
                        fullWidth
                        label={t("serviceModulesPage.statusPage.dialog.maxPointsLabel")}
                        value={maxPoints}
                        onChange={(e) => setMaxPoints(e.target.value)}
                        placeholder="∞"
                        error={!!errors.maxPoints}
                        helperText={
                            errors.maxPoints
                                ? t(errors.maxPoints)
                                : maxPointsIsOpenEnded
                                    ? t("serviceModulesPage.statusPage.dialog.openEndedHint")
                                    : undefined
                        }
                    />
                </Stack>

                <TextField
                    fullWidth
                    label={t("serviceModulesPage.statusPage.dialog.payoutPointsLabel")}
                    value={payoutPoints}
                    onChange={(e) => setPayoutPoints(e.target.value)}
                    error={!!errors.payoutPoints}
                    helperText={errors.payoutPoints ? t(errors.payoutPoints) : undefined}
                    sx={{ mt: 3 }}
                />

                {!!errors.range && (
                    <Typography color="error" variant="caption" sx={{ mt: 2, display: "block" }}>
                        {t(errors.range)}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <GeneralButton appVariant="secondary" onClick={props.onClose}>
                    {t("buttons.cancel")}
                </GeneralButton>
                <GeneralButton appVariant="primary" onClick={handleSave}>
                    {t("buttons.save")}
                </GeneralButton>
            </DialogActions>
        </Dialog>
    );
}
